import React from 'react';
import ReactMarkdown from 'react-markdown';
import { Typography, Table, TableHead, TableBody, TableRow, TableCell, Paper, styled } from '@mui/material';
import { TypographyProps } from '@mui/material/Typography';
import { CodeProps } from 'react-markdown/lib/ast-to-react';

export const InlineCode = styled('span')({
  backgroundColor: '#f2f2f2',
  borderRadius: '3px',
  padding: '0.2em 0.4em',
  fontFamily: 'monospace',
  maxWidth: '100%',
  overflowWrap: 'break-word',
  wordWrap: 'break-word',
});

// Add a styled wrapper to control overall markdown spacing
const MarkdownWrapper = styled('div')({
  '& > *:last-child': {
    marginBottom: 0,
  },
  maxWidth: '100%',
  // Add markdown-body class to target for styling
  '&.markdown-body': {
    width: '100%',
    overflowWrap: 'break-word',
    wordWrap: 'break-word',
  },
  // Ensure tables don't overflow
  '& table': {
    maxWidth: '100%',
    display: 'block',
    overflowX: 'auto',
  },
  // Ensure code blocks don't overflow
  '& pre': {
    maxWidth: '100%',
    overflowX: 'auto',
    whiteSpace: 'pre-wrap',
    wordWrap: 'break-word',
  },
  // Ensure long links break properly
  '& a': {
    wordBreak: 'break-word',
    overflowWrap: 'break-word',
  },
});

type MarkdownVariant = TypographyProps['variant'];

const LinkRenderer = ({ ...props }) => <a {...props} target="_blank" rel="noopener noreferrer" />

const CodeRenderer: React.FC<CodeProps> = ({ node, inline, className, children, ...props }) => {
  return inline ? (
    <InlineCode {...props}>{children}</InlineCode>
  ) : (
    <pre style={{
      whiteSpace: 'pre-wrap',
      wordWrap: 'break-word',
      backgroundColor: '#f5f5f5',
      padding: '16px',
      borderRadius: '4px',
      marginBottom: '16px',
      marginTop: '8px',
      maxWidth: '100%',
      overflowX: 'auto'
    }}>
      <code className={className} {...props} style={{ overflowWrap: 'break-word' }}>{children}</code>
    </pre>
  );
};

const typographyStyles = {
  maxWidth: '100%',
  overflowWrap: 'break-word',
  wordWrap: 'break-word',
};

const components = (variant: MarkdownVariant) => {
  return {
    a: LinkRenderer,
    h1: ({ children }) => <Typography variant="h3" sx={{ pt: 2, pb: 2, ...typographyStyles }}>{children}</Typography>,
    h2: ({ children }) => <Typography variant="h4" sx={{ pt: 2, pb: 2, ...typographyStyles }}>{children}</Typography>,
    h3: ({ children }) => <Typography variant="h5" sx={{ pt: 2, pb: 2, ...typographyStyles }}>{children}</Typography>,
    h4: ({ children }) => <Typography variant="h6" sx={{ pt: 2, pb: 2, ...typographyStyles }}>{children}</Typography>,
    h5: ({ children }) => <Typography variant="subtitle1" sx={{ pt: 2, pb: 2, ...typographyStyles }}>{children}</Typography>,
    h6: ({ children }) => <Typography variant="subtitle2" sx={{ pt: 2, pb: 2, ...typographyStyles }}>{children}</Typography>,
    p: ({ children }) => <Typography variant={variant} sx={{ mb: 1.5, whiteSpace: 'pre-line', ...typographyStyles }}>{children}</Typography>,
    em: ({ children }) => <Typography variant={variant} component="em" sx={typographyStyles}>{children}</Typography>,
    strong: ({ children }) => <Typography variant={variant} component="strong" sx={typographyStyles}>{children}</Typography>,
    blockquote: ({ children }) => (
      <Paper elevation={3} sx={{ p: 2, my: 2.5, maxWidth: '100%', overflow: 'auto' }}>
        <Typography variant="body2" component="blockquote" sx={typographyStyles}>{children}</Typography>
      </Paper>
    ),
    ul: ({ children }) => <Typography variant={variant} component="ul" sx={{ mb: 2, ...typographyStyles }}>{children}</Typography>,
    ol: ({ children }) => <Typography variant={variant} component="ol" sx={{ mb: 2, ...typographyStyles }}>{children}</Typography>,
    li: ({ children }) => <Typography variant={variant} component="li" sx={{ mb: 0.5, ...typographyStyles }}>{children}</Typography>,
    code: CodeRenderer,
    table: ({ children }) => <Table sx={{ mb: 2.5, maxWidth: '100%', display: 'block', overflowX: 'auto' }}>{children}</Table>,
    thead: ({ children }) => <TableHead>{children}</TableHead>,
    tbody: ({ children }) => <TableBody>{children}</TableBody>,
    tr: ({ children }) => <TableRow>{children}</TableRow>,
    th: ({ children }) => <TableCell sx={typographyStyles}>{children}</TableCell>,
    td: ({ children }) => <TableCell sx={typographyStyles}>{children}</TableCell>,
  }
};

interface MarkdownProps {
  variant?: MarkdownVariant;
  children: string;
}

const Markdown = (props: MarkdownProps) => {
  return (
    <MarkdownWrapper className="markdown-body">
      <ReactMarkdown components={components(props.variant || 'body1')}>{props.children}</ReactMarkdown>
    </MarkdownWrapper>
  );
};

export function removeMarkdownCodeBlock(text: string): string {
  // This regex matches the ```markdown at the start and the ``` at the end
  const regex = /^```markdown\s+|\s+```$/g;
  const ret = text.replace(regex, '');
  return ret
}

export default Markdown;
